import React, { useMemo } from 'react';
import { Calendar, Building, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface UseCaseContent {
  questions_and_answers?: Array<{
    question_id: string;
    question: string;
    selected_choices: string[];
  }>;
  generated_use_case?: string;
  generation_metadata?: {
    generated_at?: string;
    generation_method?: string;
    ai_model?: string;
    promoter_edited?: boolean;
    last_edited_at?: string;
  };
}

interface UseCaseViewerProps {
  content: string | UseCaseContent;
  promoterName?: string;
  companyName?: string;
  companySize?: string;
  createdAt?: string;
  showQuestionsAndAnswers?: boolean;
  className?: string;
}

const UseCaseViewer: React.FC<UseCaseViewerProps> = ({
  content,
  promoterName,
  companyName,
  companySize,
  createdAt,
  showQuestionsAndAnswers = true,
  className = ''
}) => {
  const { t, i18n } = useTranslation();
  const [showQA, setShowQA] = React.useState(false);

  const parsedContent = useMemo(() => {
    if (typeof content === 'string') {
      try {
        return JSON.parse(content) as UseCaseContent;
      } catch (e) {
        // If parsing fails, return the string as the generated use case
        return {
          generated_use_case: content,
          questions_and_answers: [],
          generation_metadata: {}
        };
      }
    }
    return content;
  }, [content]);

  const formattedDate = useMemo(() => {
    if (!createdAt) return null;
    try {
      return new Date(createdAt).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return null;
    }
  }, [createdAt, i18n.language]);

  // Format the use case text with proper paragraphs
  const formattedUseCase = useMemo(() => {
    const text = parsedContent.generated_use_case || '';
    // Split by double newlines to create paragraphs
    return text.split('\n\n').filter(p => p.trim());
  }, [parsedContent]);

  return (
    <div className={`bg-white rounded-lg ${className}`}>
      {/* Header Section */}
      {(promoterName || companyName) && (
        <div className="border-b border-gray-200 pb-4 mb-6">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            {promoterName && (
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold mr-2">
                  {promoterName.split(' ').map(n => n[0]).join('')}
                </div>
                <span className="font-medium text-gray-900">{promoterName}</span>
              </div>
            )}
            {companyName && (
              <div className="flex items-center">
                <Building className="w-4 h-4 mr-1" />
                <span>{companyName}</span>
                {companySize && <span className="ml-1 text-gray-400">({companySize})</span>}
              </div>
            )}
            {formattedDate && (
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                <span>{formattedDate}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Use Case Content */}
      <div className="prose max-w-none">
        <div className="space-y-4">
          {formattedUseCase.map((paragraph, index) => (
            <p key={index} className="text-gray-700 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>
      </div>

      {/* Metadata Section */}
      {parsedContent.generation_metadata && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            {parsedContent.generation_metadata.ai_model && (
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-50 text-purple-700">
                {t('shared.useCaseViewer.aiGenerated')}
              </span>
            )}
            {parsedContent.generation_metadata.promoter_edited && (
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                {t('shared.useCaseViewer.editedByPromoter')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Questions and Answers Section */}
      {showQuestionsAndAnswers && parsedContent.questions_and_answers && parsedContent.questions_and_answers.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setShowQA(!showQA)}
            className="flex items-center justify-between w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <span className="font-medium text-gray-900">{t('shared.useCaseViewer.questionsAndAnswers')}</span>
            {showQA ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {showQA && (
            <div className="mt-4 space-y-4">
              {parsedContent.questions_and_answers.map((qa, index) => (
                <div key={qa.question_id || index} className="pl-4 border-l-2 border-gray-200">
                  <div className="font-medium text-gray-900 mb-2">{qa.question}</div>
                  <div className="flex flex-wrap gap-2">
                    {qa.selected_choices.map((choice, choiceIndex) => (
                      <span
                        key={choiceIndex}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-700"
                      >
                        {choice}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UseCaseViewer;
